import React, {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { ChatContainer } from '@/features/chat/shared/components/ChatContainer';
import { FileType as GlobalFileType } from '../../../types';
import { MultiFileChatProps } from './types';
import { useSessionManagement } from './hooks/useSessionManagement';
import { useAskAI } from './hooks/useAskAI';
import ChatMessages from './ChatMessages';
import InputArea from './components/InputArea';

export type { MultiFileChatRef } from './types';

const MultiFileChat = forwardRef<
  import('./types').MultiFileChatRef,
  MultiFileChatProps
>(
  (
    {
      open = false,
      externalShowHistoryDropdown,
      externalSetShowHistoryDropdown,
    },
    ref,
  ) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const [resultSearchTerm, setResultSearchTerm] = useState('');
    const [isFileDragging, setIsFileDragging] = useState(false);
    const [internalShowHistoryDropdown, setInternalShowHistoryDropdown] =
      useState(false);

    const showHistoryDropdown =
      externalShowHistoryDropdown ?? internalShowHistoryDropdown;
    const setShowHistoryDropdown =
      externalSetShowHistoryDropdown ?? setInternalShowHistoryDropdown;

    // Session management (state, CRUD, effects)
    const session = useSessionManagement();

    // AI streaming (runAskAI, edit, regenerate, stop)
    const ai = useAskAI({
      selectedFiles: session.selectedFiles,
      currentSessionId: session.currentSessionId,
      conversationHistory: session.conversationHistory,
      setConversationHistory: session.setConversationHistory,
      setCompletion: session.setCompletion,
      setStage: session.setStage,
      setIsLoadingFromHistory: session.setIsLoadingFromHistory,
      setAutoSelectedFiles: session.setAutoSelectedFiles,
      saveSessionId: session.saveSessionId,
      refreshSessions: session.refreshSessions,
    });

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        clearChat: session.clearChat,
        hasContent: () =>
          session.conversationHistory.length > 0 ||
          session.completion.length > 0 ||
          session.selectedFiles.length > 0,
        addFile: (file: GlobalFileType) => {
          const fileId = file._id;
          session.setSelectedFiles((prev) => {
            if (prev.includes(fileId)) return prev;
            session.setCachedFiles((current) => [...current, file]);
            return [...prev, fileId];
          });
        },
        removeFile: (fileId: string) => {
          session.setSelectedFiles((prev) => {
            if (!prev.includes(fileId)) return prev;
            session.setCachedFiles((current) =>
              current.filter((f) => f._id !== fileId),
            );
            return prev.filter((id) => id !== fileId);
          });
        },
        focusInput: () => {
          setTimeout(() => {
            session.inputRef.current?.focus();
          }, 0);
        },
      }),
      [
        session.clearChat,
        session.conversationHistory.length,
        session.completion.length,
        session.selectedFiles.length,
      ],
    );

    // File selection handler
    const handleFileSelect = useCallback(
      (file: GlobalFileType) => {
        // Clear all signal (empty object from InputArea chip clear)
        if (!file._id) {
          session.setSelectedFiles([]);
          session.setCachedFiles([]);
          return;
        }

        const fileId = file._id;
        session.setSelectedFiles((prev) => {
          if (prev.includes(fileId)) {
            session.setCachedFiles((current) =>
              current.filter((f) => f._id !== fileId),
            );
            return prev.filter((id) => id !== fileId);
          }
          session.setCachedFiles((current) => [...current, file]);
          return [...prev, fileId];
        });
      },
      [session],
    );

    // Copy handler
    const handleCopyContent = useCallback((cleanedText: string) => {
      if (cleanedText) {
        navigator.clipboard.writeText(cleanedText);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    }, []);

    // Drag and drop
    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          const fileData = e.dataTransfer.getData('application/json');
          if (fileData) {
            const file: GlobalFileType = JSON.parse(fileData);
            const fileId = file._id;
            session.setSelectedFiles((prev) => {
              if (prev.includes(fileId)) return prev;
              session.setCachedFiles((current) => [...current, file]);
              setTimeout(() => {
                session.inputRef.current?.focus();
              }, 100);
              return [...prev, fileId];
            });
          }
        } catch (error) {
          console.error('Failed to parse dropped file data:', error);
        }
      },
      [session],
    );

    // Global drag detection
    useEffect(() => {
      const handleGlobalDragStart = (e: DragEvent) => {
        if (e.dataTransfer?.types.includes('application/json')) {
          setIsFileDragging(true);
        }
      };
      const handleGlobalDragEnd = () => setIsFileDragging(false);

      document.addEventListener('dragstart', handleGlobalDragStart);
      document.addEventListener('dragend', handleGlobalDragEnd);
      document.addEventListener('drop', handleGlobalDragEnd);
      return () => {
        document.removeEventListener('dragstart', handleGlobalDragStart);
        document.removeEventListener('dragend', handleGlobalDragEnd);
        document.removeEventListener('drop', handleGlobalDragEnd);
      };
    }, []);

    if (!open) return null;

    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="relative w-full h-full"
      >
        <ChatContainer layout="fullscreen">
          <ChatMessages
            completion={session.completion}
            isLoading={ai.isLoading}
            stage={session.stage}
            loadingStage={ai.loadingStage}
            files={{ data: session.cachedFiles }}
            hideReferences={false}
            resultSearchTerm={resultSearchTerm}
            setResultSearchTerm={setResultSearchTerm}
            onCopyContent={handleCopyContent}
            copySuccess={copySuccess}
            conversationHistory={session.conversationHistory}
            hideToolbar={false}
            onClearCompletion={session.clearChat}
            onEditQuery={ai.handleEditQuery}
            onRegenerateResponse={ai.handleRegenerateResponse}
            isFileDragging={isFileDragging}
            isLoadingFromHistory={session.isLoadingFromHistory}
            toolCallSteps={ai.toolCallSteps}
            isSearchingFiles={ai.isSearchingFiles}
            autoSelectedSources={session.autoSelectedFiles.map((f) => ({
              id: f._id,
              name: f.name,
              thumbUrl: f.thumbUrl,
              fileType: (f as any).contentType || (f as any).mimeType,
              parentId: f.parentId,
            }))}
            pendingScreenshots={ai.pendingScreenshots}
          />
          <ChatContainer.Input>
            <InputArea
              directions={ai.directions}
              setDirections={ai.setDirections}
              onSubmit={ai.runAskAI}
              stage={session.stage}
              isLoading={ai.isLoading}
              onStopLoading={ai.stopGeneration}
              inputRef={session.inputRef}
              selectedFiles={session.selectedFiles}
              cachedFiles={session.cachedFiles}
              onFileSelect={handleFileSelect}
              showHistoryDropdown={showHistoryDropdown}
              setShowHistoryDropdown={setShowHistoryDropdown}
              externalShowHistoryDropdown={externalShowHistoryDropdown}
              savedConversations={session.savedConversations}
              isLoadingSessions={session.isLoadingSessions}
              onLoadConversation={session.loadConversation}
              onDeleteConversation={session.deleteConversation}
            />
          </ChatContainer.Input>
        </ChatContainer>
      </div>
    );
  },
);

MultiFileChat.displayName = 'MultiFileChat';

export default MultiFileChat;

// Page wrapper component for use in routes
export const MultiFileChatPage: React.FC = () => {
  const [isOpen] = useState(true);

  return (
    <div className="flex w-[calc(100%-32px)] h-[calc(100vh-64px-16px)] m-[8px_16px_8px_16px] mt-0 relative top-0 bg-background rounded-2xl overflow-hidden">
      <MultiFileChat open={isOpen} />
    </div>
  );
};
